import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { BoltIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import PageHeader from '../../components/ui/PageHeader.tsx';
import StatusChip from '../../components/ui/StatusChip.tsx';
import { useAuth } from '../../contexts/AuthContext.tsx';

type Rule = {
  id: string;
  ruleType: string;
  enabled: boolean;
  label: string;
  description: string;
  direction: 'in' | 'out';
  origemHint: string;
};

const LancamentosAutomaticos: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'historico' ? 'historico' : 'regras';
  const [localRules, setLocalRules] = useState<Rule[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data: rulesData, isLoading: loadingRules } = useQuery(
    ['auto-rules'],
    async () => {
      const res = await api.get('/financeiro/lancamentos-automaticos/regras');
      return res.data as { rules: Rule[]; summary: any };
    }
  );

  const { data: historyData, isLoading: loadingHistory } = useQuery(
    ['auto-history', tab],
    async () => {
      const res = await api.get('/financeiro/lancamentos-automaticos/historico');
      return res.data as { items: any[]; total: number };
    },
    { enabled: tab === 'historico' }
  );

  useEffect(() => {
    if (rulesData?.rules) {
      setLocalRules(rulesData.rules);
      setDirty(false);
    }
  }, [rulesData]);

  const saveMutation = useMutation(
    async () => {
      const res = await api.put('/financeiro/lancamentos-automaticos/regras', {
        rules: localRules.map(r => ({ ruleType: r.ruleType, enabled: r.enabled })),
      });
      return res.data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('auto-rules');
        setDirty(false);
      },
    }
  );

  const activeCount = useMemo(() => localRules.filter(r => r.enabled).length, [localRules]);
  const canWrite = hasPermission('debts.create') || hasPermission('debts.update');

  const setTab = (t: 'regras' | 'historico') => {
    setParams(t === 'historico' ? { tab: 'historico' } : {});
  };

  const toggle = (ruleType: string) => {
    setLocalRules(prev =>
      prev.map(r => (r.ruleType === ruleType ? { ...r, enabled: !r.enabled } : r))
    );
    setDirty(true);
  };

  if (loadingRules) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  const summary = rulesData?.summary;
  const historyEmpty = tab === 'historico' && !loadingHistory && (historyData?.items || []).length === 0;
  const noActiveRules = activeCount === 0;

  return (
    <div>
      <PageHeader
        breadcrumb="Financeiro / Lançamentos automáticos"
        title="Lançamentos automáticos"
        subtitle="Regras que geram entradas/saídas no fluxo"
        actions={
          tab === 'regras' ? (
            canWrite ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!dirty || saveMutation.isLoading}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isLoading ? 'Salvando…' : 'Salvar regras'}
              </button>
            ) : undefined
          ) : (
            canWrite ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/financeiro/lancamentos-automaticos/executar')}
              >
                <BoltIcon style={{ width: 18, height: 18 }} />
                Executar agora
              </button>
            ) : undefined
          )
        }
      />

      <div className="tabs" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={`tab${tab === 'regras' ? ' active' : ''}`}
          onClick={() => setTab('regras')}
        >
          Regras
        </button>
        <button
          type="button"
          className={`tab${tab === 'historico' ? ' active' : ''}`}
          onClick={() => setTab('historico')}
        >
          Histórico
        </button>
      </div>

      <div className="dep-banner" style={{ marginBottom: 16 }}>
        Depende do <strong>Fluxo de caixa</strong>. Cada lançamento gerado referencia a origem e não
        duplica a mesma competência + origem. Desligar uma regra <strong>não</strong> apaga
        lançamentos já criados.
      </div>

      {tab === 'regras' && (
        <>
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi">
              <div className="kpi-label">Regras ativas</div>
              <div className="kpi-value">{activeCount}</div>
              <div className="kpi-hint">de {localRules.length} tipos disponíveis</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Gerados neste mês</div>
              <div className="kpi-value">{summary?.generatedThisMonth ?? 0}</div>
              <div className="kpi-hint">lançamentos no fluxo</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Próxima execução</div>
              <div className="kpi-value" style={{ fontSize: 18 }}>
                {summary?.nextRunDate
                  ? new Date(summary.nextRunDate + 'T00:00:00').toLocaleDateString('pt-BR')
                  : '—'}
              </div>
              <div className="kpi-hint">competência seguinte</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Tipos de regra</div>
              <span className="muted">Toggle independente por tipo</span>
            </div>
            {localRules.map(rule => (
              <div className="toggle-row" key={rule.ruleType}>
                <div className="toggle-info">
                  <div className="toggle-title">{rule.label}</div>
                  <div className="toggle-desc">{rule.description}</div>
                  <div className="rule-meta" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    <StatusChip
                      label={rule.enabled ? 'Ativa' : 'Desligada'}
                      tone={rule.enabled ? 'green' : 'slate'}
                    />
                    <StatusChip
                      label={rule.direction === 'in' ? 'Entrada' : 'Saída'}
                      tone={rule.direction === 'in' ? 'blue' : 'red'}
                    />
                    <span className="muted">Origem: {rule.origemHint}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className={`toggle${rule.enabled ? ' on' : ''}`}
                  aria-pressed={rule.enabled}
                  disabled={!canWrite}
                  onClick={() => toggle(rule.ruleType)}
                  title={rule.enabled ? 'Ligado' : 'Desligado'}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'historico' && (
        <>
          {noActiveRules && historyEmpty ? (
            <div className="card" style={{ padding: 48, textAlign: 'center' }}>
              <div className="page-title" style={{ fontSize: 20, marginBottom: 8 }}>
                Nenhuma regra ativa
              </div>
              <p className="muted" style={{ marginBottom: 20 }}>
                Ative ao menos uma regra para gerar lançamentos automáticos no fluxo de caixa.
              </p>
              <button type="button" className="btn btn-primary" onClick={() => setTab('regras')}>
                Ativar regras
              </button>
            </div>
          ) : loadingHistory ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" />
            </div>
          ) : (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Histórico de geração</div>
                <span className="muted">{historyData?.total || 0} lançamento(s)</span>
              </div>
              {(historyData?.items || []).length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center' }} className="muted">
                  Nenhum lançamento automático gerado ainda.
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="data-table compact-mobile">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Descrição</th>
                        <th>Origem</th>
                        <th>Lançamento</th>
                        <th style={{ textAlign: 'right' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(historyData?.items || []).map((item: any) => (
                        <tr key={item.id}>
                          <td className="mono">
                            {new Date(item.movementDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                          </td>
                          <td>
                            <div>{item.description}</div>
                            <div className="muted">{item.regraTipo}</div>
                          </td>
                          <td>
                            {item.originBadge ? (
                              <span className="chip chip-slate">{item.originBadge}</span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => navigate('/financeiro/fluxo-de-caixa')}
                            >
                              {item.lcCode}
                            </button>
                          </td>
                          <td
                            className={`amount ${item.type === 'in' ? 'in' : 'out'}`}
                            style={{ textAlign: 'right' }}
                          >
                            {item.type === 'in' ? '+ ' : '− '}
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LancamentosAutomaticos;
